// src/components/shops/postTemplates/ContactSheet.tsx
//
// A strip of film: photos edge to edge on a dark ground, each with its frame
// number, and the words underneath rather than fighting for the same space.
//
// This is the multi-photo template. Four ordinary phone photos of an event
// look like a set here, where in a grid of rounded cards they look like four
// separate posts someone forgot to write captions for.

import { cachedUrl } from '../../../lib/storageUrl'
import SocialBrewBadge from '../SocialBrewBadge'
import { categoryLabel, type TemplateProps } from './types'

export default function ContactSheet({
  title, body, photos, category, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const label = categoryLabel(category) || 'From the shop'
  const shots = photos.slice(0, 4)
  // One photo would be a portrait, not a sheet, so a lone shot runs wide.
  const cols = shots.length === 1 ? 1 : 2

  return (
    <div
      className="overflow-hidden"
      style={{ background: '#120c08', borderRadius: px(3) }}
      data-shop={shopName}
    >
      {shots.length > 0 && (
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: px(3), padding: px(3) }}
        >
          {shots.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <div style={{ aspectRatio: shots.length === 1 ? '3 / 2' : '1 / 1', overflow: 'hidden' }}>
                <img src={cachedUrl(url)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
              {/* Frame number, in the corner where a contact sheet carries it */}
              <span
                style={{
                  position: 'absolute',
                  left: px(6),
                  bottom: px(5),
                  fontSize: px(8),
                  letterSpacing: '0.18em',
                  fontWeight: 700,
                  color: '#f0a24a',
                  textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: `${px(16)} ${px(15)} ${px(14)}` }}>
        <p
          style={{
            fontSize: px(8.5),
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(240,226,203,0.5)',
            fontWeight: 700,
            marginBottom: px(7),
          }}
        >
          {label}
        </p>
        <h3
          className="font-display"
          style={{ fontSize: px(19), lineHeight: 1.18, color: '#f4ead9', margin: 0, fontWeight: 700, textWrap: 'balance' }}
        >
          {title}
        </h3>
        {body && (
          <p style={{ fontSize: px(12.5), lineHeight: 1.6, color: 'rgba(240,226,203,0.66)', marginTop: px(8) }}>
            {body}
          </p>
        )}

        <div style={{ marginTop: px(16) }}>
          <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="dark" scale={scale} />
        </div>
      </div>
    </div>
  )
}
